/**
 * Canvas2D renderer. Reads interpolated state only; never touches the sim.
 *
 * World coordinates are metres with +Y up and the origin at the bottom-left of
 * the court. This is the single place that converts to device pixels.
 */
import type { BallState, BlobState, Constants, MatchState, SimState } from '@office/sim';
import { TEAM_NAMES, indexWithinSide, teamColor, tintFor } from './colors.js';

const PALETTE = {
  sky: '#101725',
  skyGlow: '#1b2740',
  ground: '#2f3d55',
  line: 'rgba(255,255,255,0.16)',
  net: '#e8ecf5',
  ball: '#ffd25f',
  ballSeam: 'rgba(90,60,0,0.55)',
  energy: '#ffd25f',
  eye: '#101725',
  text: 'rgba(255,255,255,0.72)',
};

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function lerpBall(a: BallState, b: BallState, t: number): BallState {
  return {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    vx: lerp(a.vx, b.vx, t),
    vy: lerp(a.vy, b.vy, t),
    spin: lerp(a.spin, b.spin, t),
    angle: lerp(a.angle, b.angle, t),
  };
}

function lerpBlob(a: BlobState, b: BlobState, t: number): BlobState {
  return {
    ...b,
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    vx: lerp(a.vx, b.vx, t),
    vy: lerp(a.vy, b.vy, t),
  };
}

export interface RenderOptions {
  /** Draw the net. Off until step 2. */
  net?: boolean;
  /** Control hints, drawn along the bottom. */
  hud?: string;
  /** Draw the scoreboard for this match state. */
  match?: MatchState;
  /** Extra reserved space at the bottom, e.g. for on-screen controls. */
  bottomInset?: number;
  /** Which blobs are spending energy this frame, by slot. */
  charged?: readonly boolean[];
}

export class CourtRenderer {
  private ctx: CanvasRenderingContext2D;
  private scale = 1;
  private originX = 0;
  private originY = 0;
  private cssWidth = 0;
  private cssHeight = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly constants: Constants,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas2D context unavailable');
    this.ctx = ctx;
    this.resize();
  }

  /** Match the backing store to the element size and device pixel ratio. */
  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width));
    const h = Math.max(1, Math.round(rect.height));
    this.cssWidth = w;
    this.cssHeight = h;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /**
   * Vertical space reserved for the scoreboard and the control hints.
   *
   * Proportional rather than fixed: 94 px of furniture is nothing on a desktop
   * and a third of the screen on a phone held sideways, which would squeeze the
   * court down to a strip.
   */
  private bands(hasHud: boolean, bottomInset = 0): { top: number; bottom: number } {
    const top = clamp(this.cssHeight * 0.1, 34, 64);
    const bottom = (hasHud ? clamp(this.cssHeight * 0.05, 16, 30) : 8) + bottomInset;
    return { top, bottom };
  }

  private computeViewport(hasHud: boolean, bottomInset: number): void {
    const c = this.constants;
    const { top, bottom } = this.bands(hasHud, bottomInset);
    const availW = this.cssWidth * 0.94;
    const availH = Math.max(40, this.cssHeight - top - bottom);
    this.scale = Math.min(availW / c.courtWidth, availH / c.courtHeight);
    this.originX = (this.cssWidth - c.courtWidth * this.scale) / 2;
    this.originY = top + (availH + c.courtHeight * this.scale) / 2;
  }

  private px(x: number): number {
    return this.originX + x * this.scale;
  }

  private py(y: number): number {
    return this.originY - y * this.scale;
  }

  draw(prev: SimState, next: SimState, alpha: number, opts: RenderOptions = {}): void {
    // Mobile browsers change the viewport without a resize event (URL bar
    // collapse, rotation on some Safaris). A stale backing store draws the
    // court stretched, so re-fit whenever the element size disagrees.
    if (
      this.canvas.clientWidth !== this.cssWidth ||
      this.canvas.clientHeight !== this.cssHeight
    ) {
      this.resize();
    }
    const t = Math.min(1, Math.max(0, alpha));
    const ball = lerpBall(prev.ball, next.ball, t);
    const blobs = next.blobs.map((b, i) => {
      const p = prev.blobs[i];
      return p ? lerpBlob(p, b, t) : b;
    });

    this.computeViewport(Boolean(opts.hud), opts.bottomInset ?? 0);
    const ctx = this.ctx;
    const c = this.constants;

    ctx.clearRect(0, 0, this.cssWidth, this.cssHeight);

    // Backdrop
    const grad = ctx.createLinearGradient(0, 0, 0, this.cssHeight);
    grad.addColorStop(0, PALETTE.sky);
    grad.addColorStop(1, PALETTE.skyGlow);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, this.cssWidth, this.cssHeight);

    // Court box
    const left = this.px(0);
    const right = this.px(c.courtWidth);
    const top = this.py(c.courtHeight);
    const floor = this.py(0);

    ctx.fillStyle = 'rgba(255,255,255,0.03)';
    ctx.fillRect(left, top, right - left, floor - top);
    ctx.strokeStyle = PALETTE.line;
    ctx.lineWidth = 1;
    ctx.strokeRect(left + 0.5, top + 0.5, right - left - 1, floor - top - 1);

    // Ground slab
    ctx.fillStyle = PALETTE.ground;
    ctx.fillRect(left, floor, right - left, Math.max(4, this.cssHeight - floor));

    if (opts.net) this.drawNet();
    this.drawBallShadow(ball);
    blobs.forEach((blob, i) =>
      this.drawBlob(blob, tintFor(blob.side, indexWithinSide(blobs, i)), opts.charged?.[i] === true),
    );
    this.drawBall(ball);

    if (opts.match) this.drawScoreboard(opts.match, this.bands(Boolean(opts.hud)).top);

    if (opts.hud) {
      ctx.font = '500 12px ui-monospace, SFMono-Regular, Menlo, monospace';
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(opts.hud, this.cssWidth / 2, this.cssHeight - 8);
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
    }
  }

  /** Score, touch pips for the side in possession, and the phase banner. */
  private drawScoreboard(match: MatchState, band: number): void {
    const ctx = this.ctx;
    const cx = this.cssWidth / 2;
    const y = Math.round(band * 0.1);
    // Everything in the band scales with it, so the scoreboard never crowds a
    // short screen or looks lost on a tall one.
    const score = clamp(band * 0.46, 17, 30);
    const gap = clamp(band * 0.34, 13, 22);

    ctx.textBaseline = 'top';
    ctx.font = `700 ${score}px ui-monospace, SFMono-Regular, Menlo, monospace`;

    ctx.textAlign = 'right';
    ctx.fillStyle = teamColor(0);
    ctx.fillText(String(match.score[0]), cx - gap, y);

    ctx.textAlign = 'left';
    ctx.fillStyle = teamColor(1);
    ctx.fillText(String(match.score[1]), cx + gap, y);

    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.font = `600 ${score * 0.73}px ui-monospace, SFMono-Regular, Menlo, monospace`;
    ctx.fillText('–', cx, y + score * 0.13);

    // Touch pips: how much of the three-touch allowance is spent.
    if (match.touchSide !== null && match.touches > 0) {
      const color = teamColor(match.touchSide);
      const dir = match.touchSide === 0 ? -1 : 1;
      const r = Math.max(3, score * 0.13);
      for (let i = 0; i < match.touches; i++) {
        ctx.beginPath();
        ctx.arc(cx + dir * (gap * 3.4 + i * r * 3.5), y + score * 0.53, r, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
      }
    }

    const banner = bannerFor(match);
    if (banner) {
      ctx.font = `600 ${clamp(band * 0.22, 11, 14)}px ui-monospace, SFMono-Regular, Menlo, monospace`;
      ctx.fillStyle = PALETTE.text;
      ctx.fillText(banner, cx, y + score * 1.26);
    }
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  private drawNet(): void {
    const c = this.constants;
    const ctx = this.ctx;
    const w = Math.max(2, c.netThickness * this.scale);
    const x = this.px(c.courtWidth / 2) - w / 2;
    const top = this.py(c.netHeight);
    const bottom = this.py(0);
    ctx.fillStyle = PALETTE.net;
    ctx.globalAlpha = 0.85;
    ctx.fillRect(x, top, w, bottom - top);
    ctx.globalAlpha = 1;
    // Cap band, so the tape line reads clearly at a glance.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x - w * 0.6, top, w * 2.2, Math.max(3, w * 0.9));
  }

  private drawBlob(blob: BlobState, color: string, charged: boolean): void {
    const ctx = this.ctx;
    const r = this.constants.blobRadius * this.scale;
    const cx = this.px(blob.x);
    const cy = this.py(blob.y);

    // Contact shadow on the floor
    const floor = this.py(0);
    const lift = Math.max(0, floor - cy);
    const shrink = Math.max(0.35, 1 - lift / (r * 6));
    ctx.save();
    ctx.globalAlpha = 0.22 * shrink;
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.ellipse(cx, floor + 2, r * 0.85 * shrink, r * 0.22 * shrink, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // A charged blob glows: the one visual cue that the next touch will hurt.
    if (charged) {
      ctx.save();
      ctx.shadowColor = PALETTE.energy;
      ctx.shadowBlur = r * 0.9;
      ctx.fillStyle = PALETTE.energy;
      ctx.globalAlpha = 0.55;
      ctx.beginPath();
      ctx.arc(cx, cy, r * 1.06, Math.PI, 0, false);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(cx, cy, r, Math.PI, 0, false);
    ctx.closePath();
    ctx.fill();

    this.drawEnergy(blob, color);

    // Highlight
    ctx.save();
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.ellipse(cx - r * 0.3, cy - r * 0.5, r * 0.28, r * 0.18, -0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Eye, looking the way the blob is moving
    const look = Math.max(-1, Math.min(1, blob.vx / 6));
    const facing = blob.side === 0 ? 1 : -1;
    const ex = cx + (look * 0.18 + facing * 0.22) * r;
    const ey = cy - r * 0.45;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(ex, ey, r * 0.17, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = PALETTE.eye;
    ctx.beginPath();
    ctx.arc(ex + look * r * 0.05, ey, r * 0.09, 0, Math.PI * 2);
    ctx.fill();
  }

  /** A thin bar under the blob's feet: how much smash is left in it. */
  private drawEnergy(blob: BlobState, color: string): void {
    const ctx = this.ctx;
    const r = this.constants.blobRadius * this.scale;
    const w = r * 1.5;
    const h = Math.max(2, r * 0.09);
    const x = this.px(blob.x) - w / 2;
    const y = this.py(0) + Math.max(5, r * 0.22);
    ctx.save();
    ctx.globalAlpha = 0.28;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x, y, w, h);
    ctx.globalAlpha = blob.energy > 0 ? 0.95 : 0.4;
    ctx.fillStyle = blob.energy > 0 ? color : '#ffffff';
    ctx.fillRect(x, y, w * Math.max(0, Math.min(1, blob.energy)), h);
    ctx.restore();
  }

  private drawBallShadow(ball: BallState): void {
    const ctx = this.ctx;
    const r = this.constants.ballRadius * this.scale;
    const floor = this.py(0);
    const cy = this.py(ball.y);
    const lift = Math.max(0, floor - cy);
    const shrink = Math.max(0.25, 1 - lift / (r * 22));
    ctx.save();
    ctx.globalAlpha = 0.25 * shrink;
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.ellipse(this.px(ball.x), floor + 2, r * 1.1 * shrink, r * 0.3 * shrink, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawBall(ball: BallState): void {
    const ctx = this.ctx;
    const r = this.constants.ballRadius * this.scale;
    const cx = this.px(ball.x);
    const cy = this.py(ball.y);

    ctx.save();
    ctx.translate(cx, cy);
    // Canvas Y is down, so a positive (counter-clockwise) spin rotates negative.
    ctx.rotate(-ball.angle);

    ctx.fillStyle = PALETTE.ball;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();

    // Seams make the spin visible, which matters for reading a curved shot.
    ctx.strokeStyle = PALETTE.ballSeam;
    ctx.lineWidth = Math.max(1, r * 0.14);
    ctx.beginPath();
    ctx.moveTo(-r, 0);
    ctx.lineTo(r, 0);
    ctx.moveTo(0, -r);
    ctx.lineTo(0, r);
    ctx.stroke();
    ctx.restore();
  }
}

function bannerFor(match: MatchState): string {
  switch (match.phase) {
    case 'over':
      return `${sideName(match.winner)} wins  ${match.score[0]} - ${match.score[1]}  -  R to rematch`;
    case 'point': {
      const who = sideName(match.lastPointWinner);
      return match.lastFault === 'touches'
        ? `${who} scores - four touches`
        : `${who} scores`;
    }
    case 'serve':
      return `${sideName(match.server)} to serve`;
    default:
      return '';
  }
}

function sideName(side: 0 | 1 | null): string {
  return side === null ? '' : TEAM_NAMES[side];
}
